<template>
  <div class="csc-card">
    <div class="csc-card__header">
      <div class="csc-card__title">
        {{ title }}
        <slot name="title-extra" />
      </div>
      <div v-if="currencies.length" class="csc-switch" role="group" :aria-label="`${title} currency`">
        <button
          v-for="option in ['All', ...currencies]"
          :key="option"
          type="button"
          class="csc-switch__option"
          :class="{ 'csc-switch__option--active': selected === option }"
          @click="selected = option"
        >
          {{ option }}
        </button>
      </div>
    </div>

    <div class="csc-headline">
      <span class="csc-headline__label">{{ headline }}</span>
      <span class="csc-headline__value">
        {{ formattedAmount }}
        <span v-if="amountVariance" :class="varianceClass(amountVariance)" class="csc-variance">
          {{ varianceText(amountVariance) }}
        </span>
      </span>
    </div>
    <div class="csc-caption">
      <template v-if="selected === 'All' && currencies.length > 1">
        Total across {{ currencies.length }} currencies, not converted
      </template>
      <template v-else-if="selected !== 'All'">{{ selected }} only</template>
    </div>

    <div v-if="currencies.length" class="csc-bar" aria-hidden="true">
      <span
        v-for="(segment, index) in segments"
        :key="segment.currency"
        class="csc-bar__segment"
        :class="{ 'csc-bar__segment--dimmed': selected !== 'All' && selected !== segment.currency }"
        :style="{ width: `${segment.share}%`, background: colour(index) }"
        :title="`${segment.currency}: ${segment.formatted}`"
      />
    </div>
    <div v-if="currencies.length" class="csc-legend">
      <span v-for="(segment, index) in segments" :key="segment.currency" class="csc-legend__item">
        <span class="csc-legend__swatch" :style="{ background: colour(index) }" />
        {{ segment.currency }}
      </span>
    </div>

    <div v-for="metric in metrics" :key="metric.key" class="csc-metric">
      <span class="csc-metric__label">{{ metric.label }}</span>
      <span class="csc-metric__value">
        {{ metricValue(metric.key).toLocaleString('en-US') }}
        <span v-if="metricVariance(metric.key)" :class="varianceClass(metricVariance(metric.key))" class="csc-variance">
          {{ varianceText(metricVariance(metric.key)) }}
        </span>
      </span>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch } from '@vue/composition-api'

import { formatCurrency } from './useInvoiceExport'

// Shades of the mockup's teal, darkest first; repeats past five currencies.
const PALETTE = ['#1f4e54', '#3d7a82', '#6a9ea5', '#9cc0c4', '#c8dfe1']

// "All" mixes currencies, so it is a plain number - no symbol, no
// conversion (LIQ-2656).
const formatPlain = (value) => new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(value || 0)

/**
 * One summary card with an All / per-currency switch, the total for the
 * selection, a bar of each currency's share and count rows (LIQ-2656).
 *
 * byCurrency: { GBP: { amount, invoice_count, ... }, USD: {...} } - every
 * currency in the data. A currency whose amount is zero is hidden from the
 * switch, bar and legend, but its counts still contribute to "All": a draft
 * with amount 0 and three BDX is still one invoice and three BDX.
 * variance: optional (currency) => { [field]: { pct, direction } } for the
 * selection ('All' or a currency).
 */
export default defineComponent({
  name: 'CurrencySummaryCard',

  props: {
    title: { type: String, required: true },
    headline: { type: String, required: true },
    byCurrency: { type: Object, default: () => ({}) },
    amountKey: { type: String, default: 'amount' },
    metrics: { type: Array, default: () => [] },
    variance: { type: Function, default: null },
  },

  setup(props) {
    const selected = ref('All')

    const allCurrencies = computed(() => Object.keys(props.byCurrency || {}).sort())

    // The switch, bar and legend only offer currencies with a non-zero amount;
    // "All" still aggregates every bucket (see the component docstring).
    const currencies = computed(() => allCurrencies.value.filter(
      (ccy) => Math.round((Number(props.byCurrency[ccy][props.amountKey]) || 0) * 100) !== 0))

    // A filter can take the picked currency out of the data; fall back to All.
    watch(currencies, (list) => {
      if (selected.value !== 'All' && !list.includes(selected.value)) selected.value = 'All'
    })

    const sumOf = (key) => {
      if (selected.value !== 'All') {
        const bucket = props.byCurrency[selected.value] || {}
        return Number(bucket[key]) || 0
      }
      return allCurrencies.value.reduce((total, ccy) => total + (Number(props.byCurrency[ccy][key]) || 0), 0)
    }

    const amount = computed(() => sumOf(props.amountKey))
    const formattedAmount = computed(() =>
      selected.value === 'All' ? formatPlain(amount.value) : formatCurrency(amount.value, selected.value))

    const segments = computed(() => {
      const values = currencies.value.map((ccy) => Math.abs(Number(props.byCurrency[ccy][props.amountKey]) || 0))
      const total = values.reduce((a, b) => a + b, 0)
      return currencies.value.map((ccy, i) => ({
        currency: ccy,
        share: total ? (values[i] / total) * 100 : 0,
        formatted: formatCurrency(Number(props.byCurrency[ccy][props.amountKey]) || 0, ccy),
      }))
    })

    const varianceFor = computed(() => (props.variance ? props.variance(selected.value) || {} : {}))
    const isShown = (v) => v && v.pct !== null && v.pct !== undefined
    const amountVariance = computed(() => (isShown(varianceFor.value.amount) ? varianceFor.value.amount : null))
    const metricVariance = (key) => (isShown(varianceFor.value[key]) ? varianceFor.value[key] : null)
    const varianceText = (v) => `${v.direction === 'up' ? '▲' : '▼'} ${Math.abs(v.pct)}%`
    const varianceClass = (v) => (v.direction === 'up' ? 'csc-variance--up' : 'csc-variance--down')

    return {
      selected,
      currencies,
      formattedAmount,
      segments,
      colour: (index) => PALETTE[index % PALETTE.length],
      metricValue: sumOf,
      amountVariance,
      metricVariance,
      varianceText,
      varianceClass,
    }
  },
})
</script>

<style scoped>
.csc-card {
  background: #fff;
  border: 1px solid #e6eaea;
  border-top: 3px solid #1f4e54;
  border-radius: 6px;
  padding: 16px 20px;
  min-width: 0;
}

.csc-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.csc-card__title {
  font-weight: 600;
  color: #1f4e54;
  display: flex;
  align-items: center;
  gap: 6px;
}

.csc-switch {
  display: inline-flex;
  border: 1px solid #d5dfe0;
  border-radius: 4px;
  overflow: hidden;
}

.csc-switch__option {
  border: 0;
  border-left: 1px solid #d5dfe0;
  background: #fff;
  color: #1f4e54;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  cursor: pointer;
}

.csc-switch__option:first-child {
  border-left: 0;
}

.csc-switch__option--active {
  background: #1f4e54;
  color: #fff;
}

.csc-headline {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.csc-headline__label {
  font-size: 1.4rem;
  color: #1f4e54;
}

.csc-headline__value {
  font-size: 1.6rem;
  font-weight: 600;
  color: #1f4e54;
  white-space: nowrap;
}

.csc-caption {
  text-align: right;
  font-size: 12px;
  color: #6c7a7b;
  min-height: 18px;
  margin-bottom: 10px;
}

.csc-bar {
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background: #eef3f3;
  gap: 2px;
}

.csc-bar__segment {
  height: 100%;
  transition: opacity 0.2s;
}

.csc-bar__segment--dimmed {
  opacity: 0.25;
}

.csc-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: #4b5a5b;
  margin: 8px 0 12px;
}

.csc-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.csc-legend__swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
}

.csc-metric {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #eef1f1;
  padding: 8px 0;
  font-size: 13px;
  color: #4b5a5b;
}

.csc-metric__value {
  font-weight: 600;
  color: #1f4e54;
}

.csc-variance {
  font-size: 12px;
  font-weight: 600;
  margin-left: 6px;
}

.csc-variance--up {
  color: #1e7b4f;
}

.csc-variance--down {
  color: #b23b3b;
}
</style>
